import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { usePagedList, useDebouncedValue } from "@/lib/usePagedList";
import LoadMoreButton from "@/components/LoadMoreButton";
import AlertCard from "@/components/AlertCard";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import Select from "@/components/Select";

const TOPICS_EN = ["AI in Supply Chain", "Planning", "Inventory", "S&OP / IBP", "Sourcing / Procurement", "Manufacturing", "Warehousing / Logistics", "IT & AI Governance"];
const SEVERITIES = ["Info", "Event", "Nomination", "Indiscretion"];

const Alerts = () => {
  const { t, lang } = useLang();
  const location = useLocation();
  const initialQ = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  const [filters, setFilters] = useState({ severity: "all", category: "all", since: "all", q: initialQ });
  const dq = useDebouncedValue(filters.q);
  const { items, loading, loadingMore, hasMore, loadMore } = usePagedList("/alerts", { ...filters, q: dq.trim(), language: lang });

  const allOpt = { value: "all", label: t.filters.all };
  const categoryOpts = [allOpt, ...TOPICS_EN.map((v, i) => ({ value: v, label: t.topics[i] || v }))];
  const sevOpts = [allOpt, ...SEVERITIES.map((s) => ({ value: s, label: t.severity[s] || s }))];
  const periodOpts = [
    { value: "all", label: t.filters.periodAll },
    { value: "today", label: t.filters.periodToday },
    { value: "week", label: t.filters.periodWeek },
    { value: "month", label: t.filters.periodMonth },
    { value: "quarter", label: t.filters.periodQuarter },
    { value: "year", label: t.filters.periodYear },
  ];

  return (
    <div data-testid="alerts-page">
      <SeoHead
        lang={lang}
        path="/alerts"
        title={lang === "fr" ? "Alertes Supply Chain IA - signaux courts marché et tech" : "Supply Chain AI alerts - market & tech signals"}
        description={lang === "fr"
          ? "Signaux courts de marché, lancements produits, mouvements vendeurs et risques de gouvernance IA dans la Supply Chain. Curation Dataleo."
          : "Short market signals, product launches, vendor moves and AI governance risks in Supply Chain. Curated by Dataleo for Operations leaders."}
      />
      <PageHero
        kicker={lang === "fr" ? "Flux · Alertes" : "Feed · Alerts"}
        title={t.nav.alerts}
        description={lang === "fr" ? "Signaux courts marché et technologie. Pas de news. Des décisions." : "Short market and technology signals. Not news. Decisions."}
        accent="red"
        testId="alerts-hero"
      />
      <div className="dl-container py-10">

      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div className="flex flex-col gap-1 text-[12px] flex-1 min-w-[220px]">
          <span className="dl-overline">{t.filters.search}</span>
          <div className="flex items-center border border-[#E5E7EB] bg-white">
            <SearchIcon size={16} className="ml-3 text-[#4B5563]" />
            <input
              data-testid="filter-search"
              type="text"
              placeholder={t.filters.search}
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="flex-1 px-2 py-2 text-sm bg-transparent outline-none"
            />
          </div>
        </div>
        <Select testid="filter-severity" label={t.filters.severity || "Severity"} value={filters.severity} onChange={(v) => setFilters({ ...filters, severity: v })} options={sevOpts} />
        <Select testid="filter-category" label={t.filters.category} value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={categoryOpts} />
        <Select testid="filter-period" label={t.filters.period} value={filters.since} onChange={(v) => setFilters({ ...filters, since: v })} options={periodOpts} />
        <button
          data-testid="filter-reset"
          onClick={() => setFilters({ severity: "all", category: "all", since: "all", q: "" })}
          className="text-sm underline-offset-4 hover:underline text-[#4B5563]"
        >
          {t.filters.reset}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6" data-testid="alerts-grid">
        {items.map((a, i) => <AlertCard key={a.id} item={a} idx={i} />)}
      </div>
      {!loading && <LoadMoreButton hasMore={hasMore} loading={loadingMore} onClick={loadMore} lang={lang} testid="alerts-load-more" />}
      </div>
    </div>
  );
};

export default Alerts;
